<script setup>
// Libraries
import { storeToRefs } from 'pinia';
const { loggedIn, user, clearSession, fetch: refreshSession } = useUserSession()
// Stores
import { useHeaderStore } from '@/stores/header';
import { useWindowStore } from '@/stores/window';
const headerStore = useHeaderStore();
const windowStore = useWindowStore();
const route = useRoute()

// Variables
const { $bus } = useNuxtApp();
const { menu } = storeToRefs(headerStore);
const { innerWidth, scrollY } = storeToRefs(windowStore);

const header = ref(null);
const showMobileNav = ref(false);
const lastScrollY = ref(0);
const scrollDirection = ref('top');
const scrollThreshold = 100;
const searchOpen = ref(false);

const search_menu = [{
    'icon': 'local-nav-search',
    'to': '/search',
    'label': ''
}]

const main_menu = menu.value.map(item => ({
    ...item,
    to: item.url,
    url: undefined,
    children: item.subitems ? item.subitems.map(subitem => ({
        ...subitem,
        to: subitem.url,
        url: undefined
    })) : undefined,
    subitems: undefined
}));

watch(loggedIn, (val) => {
    console.info('Header: loggedIn changed:', val);
});

const full_menu = computed(() => {
    const user_menu = loggedIn.value ? [{
        'icon': 'local-nav-user',
        'label': user.value.firstName || 'Me',
        children: [
            { 'to': '/sessions?tab=favorites', 'label': 'My Favorites' },
            { 'to': '/sessions?tab=seats', 'label': 'My Saved Seats' },
            { 'to': '/braindate', 'label': 'Braindates' },
            { 'to': '/logout', 'label': 'Logout' }
        ]
    }] : [{
        'icon': 'local-nav-user',
        'to': '/login',
    }];

    return [...main_menu, ...search_menu, ...user_menu];
});

const mobile_menu = computed(() => {
    const user_menu = loggedIn.value ? [{
        'icon': 'local-nav-user',
        'label': user.value.firstName || 'Me',
        children: [
            { 'to': '/sessions?tab=favorites', 'label': 'My Favorites' },
            { 'to': '/sessions?tab=seats', 'label': 'My Saved Seats' },
            { 'to': '/braindate', 'label': 'Braindates' },
            { 'to': '/logout', 'label': 'Logout' }
        ]
    }] : [];
    return [...main_menu, ...user_menu];
});

// Logic
const toggleMobileNav = () => {
    showMobileNav.value = !showMobileNav.value;
};

$bus.on('close-mobile-menu', () => {
    showMobileNav.value = false;
});

$bus.on('scroll-unlock', (hasFocus) => {
    showMobileNav.value = false;
});

const setHeaderOffsets = () => {
    const headerOffset = header.value.offsetHeight;
    const headerBox = header.value.querySelector('.container').getBoundingClientRect();
    const headerHeight = headerBox.height;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--header-offset', `${headerOffset}px`);
};

const setScrollDirection = (direction) => {
    let abort = false;
    let moved = false;

    if (process.client) {
        const body = document?.body;
        abort = body?.classList.contains('scroll-locked');
        moved = !moved && document.documentElement.classList.contains('up')
    }

    if (scrollY.value <= scrollThreshold) {
        scrollDirection.value = 'top';
        document.documentElement.classList.remove('down');
        document.documentElement.classList.remove('up');
        return;
    }

    if (abort) return;

    scrollDirection.value = direction;

    if (direction === 'up') {
        $bus.emit('header-scroll-up');
        document.documentElement.classList.remove('down');
        document.documentElement.classList.add('up');
    } else {
        $bus.emit('header-scroll-down');
        document.documentElement.classList.add('down');
        document.documentElement.classList.remove('up');
    }
};

const checkScrollDirection = () => {

    const scrollDifference = Math.abs(scrollY.value - lastScrollY.value);
    if (scrollDifference < scrollThreshold) return;

    if (scrollY.value <= scrollThreshold) {
        lastScrollY.value = scrollY.value;
        setScrollDirection('top');
        return;
    }

    if (scrollY.value > lastScrollY.value) {
        setScrollDirection('down');
        lastScrollY.value = scrollY.value;
    }

    if (scrollY.value < lastScrollY.value) {
        setScrollDirection('up');
        lastScrollY.value = scrollY.value;
    }
};

const openSearch = async () => {
    await navigateTo('/search');
};

const forceShow = (hasFocus) => {
    if (hasFocus) setScrollDirection('up');
};

const skipToContent = () => {
    const content = document.getElementById('skip-nav');
    content.tabIndex = 0;
    content.focus();
    setTimeout(() => {
        content.tabIndex = -1;
    }, 32);
};

const handleLoginClick = async () => {
    await navigateTo('/login');
};

// Lifecycle Logic
watch(innerWidth, () => {
    setHeaderOffsets();
});

watch(scrollY, () => {
    checkScrollDirection();
});

watch(() => route.fullPath, () => {  
    showMobileNav.value = false;
});

</script>

<template>
    <header ref="header" class="global-header" :class="`${showMobileNav ? 'showing' : ''} ${scrollDirection}`" style="--transition: 500ms ease" v-focus="forceShow">
        <div class='mover'>
            <section class="container">
                <MessLink href="/" class="logo" label="Go the home page">
                    <MessSvg type="Logo2025Short" />
                </MessLink>
                <Transition name="desktop-nav">
                    <div class="menu-wrapper">
                        <a @click.prevent="skipToContent" href="#skip-nav" class="skip-link sr-only sr-only-focusable" aria-label="Skip to Navigation">Skip to Navigation</a>
                        <UNavigationMenu v-if="full_menu" :items="full_menu" variant="link" color="transparent" class="w-full justify-center" content-orientation="vertical"  />
                        <span id="skip-nav"></span>
                    </div>
                </Transition>
                <button class="search mobile" data-desktop="false" @click="openSearch" aria-label="Open Search Dialog">
                    <MessSvg type="navSearch" />
                </button>
                <GlobalHamburger @click="toggleMobileNav" :open="showMobileNav" :aria-label="`${showMobileNav ? 'Close' : 'Open'} the navigation drawer`" />
                <Transition>
                    <div v-if="showMobileNav" class="mobile-menu-container absolute top-full h-[calc(100dvh-var(--header-height))] w-full z-[9999] bg-teal overflow-y-scroll xl:hidden">
                        <div class='px-5'>
                            <UNavigationMenu v-if='mobile_menu' :items="mobile_menu" orientation="vertical" variant="link" color="transparent" class="w-full justify-center"  />
                        </div>
                        <div class='actions px-5'>
                            <UButton v-if="!loggedIn" icon="local-nav-user" variant="outline" color="transparent" @click="handleLoginClick">Login</UButton>
                        </div>
                    </div>
                </Transition>
            </section>
        </div>
    </header>
</template>

<style lang="scss">

html {
    --header-height: 96px;
}

.global-header {

    z-index: 99;
    width: 100%;
    height: var(--header-height);
    margin: 0 auto;

    .mover {
        width: 100%;
        height: var(--header-height);
        position: relative;

        .container {
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, 0);
            transition: var(--transition);
        }
    }

    &.down {
        .mover .container {
            position: fixed;
            transform: translate(-50%, -100%);
            transition: var(--transition);
        }
    }

    &.up {
        .mover .container {
            position: fixed;
            transform: translate(-50%, 0);
            transition: var(--transition);
        }
    }

    .actions {
        margin-top: 2.25rem;
        padding-bottom: 6.25rem;
        background-image: url('@/assets/img/backgrounds/bubble-teal-21-small.png');
        background-repeat: no-repeat;
        background-position: bottom right;
    }

    .container {
        position: relative;
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        background: white;
        background-color: var(--teal);
        border-radius: 0px 0px 10px 10px;
        margin: 0 auto;

        .logo {
            align-self: stretch;
            padding: 1rem;
            @include simple-hover;
            
            @media (min-width: $tablet) {
                padding: 1.4375rem;
            }
            
            .mess-svg {
                height: 2.125rem;
                width: 4.625rem;

                @media (min-width: $tablet) {
                    height: 3rem;
                    width: 6.1875rem;
                }
            }
        }

        .search {
            margin-left: auto;
            margin-bottom: 0.25rem;
            svg {
                width: 1.5rem;
                height: 1.125rem;
                path {
                    transition: 350ms ease;
                    color: var(--sage);
                }
            }
            &:hover,
            &:focus {
                svg path { color: var(--cream); }
            }

            @media (min-width: $tablet) {
                margin-top: 0.1875rem;
                margin-left: 0;
                margin-bottom: 0;

                svg {
                    width: 0.9375rem;
                    height: 1.0625rem;
                }
            }
        }
    }
}

@media (max-width: 1290px) {
    .global-header {
        .container {
            overflow-y: unset;
            border-radius: 0px;
            margin: 0;
            width: 100vw;
        }
        .login-button,
        .search,
        .user-menu {
            &[data-desktop='true'] {
                display: none;
            }
        }
    }
}

@media (max-width: $tablet) {
    .global-header {
        .container {
            overflow: unset;
            align-items: center;
        }
    }
}

.menu-wrapper {
    display: flex;
    align-items: center;
    align-self: center;
    height: 100%;
    padding-right: 2.0625rem;
    display: none;

    @media (min-width: $desktop) {
        display: flex;
    }
    
    &.v-hidden {
        transition: opacity 0.5s;
    }
    &.v-hidden {
        opacity: 0;
    }
}

.desktop-nav-enter-active,
.desktop-nav-leave-active {
    transition: opacity 0.5s !important;
}

.desktop-nav-enter,
.desktop-nav-leave-to {
    opacity: 0 !important;
}

.desktop-nav-enter-to,
.desktop-nav-leave {
    opacity: 1 !important;
}
</style>
